import { CalendarDays } from "lucide-react";
import { useEffect, useId, useRef, useState } from "react";

const isoToDisplay = (value) => {
  if (!value) {
    return "";
  }
  const [year, month, day] = String(value).slice(0, 10).split("-");
  if (!year || !month || !day) {
    return "";
  }
  return `${month}/${day}/${year}`;
};

const displayToIso = (value) => {
  const match = value.trim().match(/^(\d{1,2})\/(\d{1,2})\/(\d{4})$/);
  if (!match) {
    return null;
  }
  const [, rawMonth, rawDay, year] = match;
  const month = Number(rawMonth);
  const day = Number(rawDay);
  const parsed = new Date(Date.UTC(Number(year), month - 1, day));
  if (
    parsed.getUTCFullYear() !== Number(year)
    || parsed.getUTCMonth() !== month - 1
    || parsed.getUTCDate() !== day
  ) {
    return null;
  }
  return `${year}-${String(month).padStart(2, "0")}-${String(day).padStart(2, "0")}`;
};

export const TypedDateInput = ({
  "aria-label": ariaLabel,
  "data-testid": legacyDataTestId,
  dataTestId,
  disabled = false,
  id,
  onChange,
  required = false,
  value,
}) => {
  const resolvedTestId = dataTestId || legacyDataTestId;
  const generatedId = useId();
  const inputId = id || generatedId;
  const calendarRef = useRef(null);
  const textRef = useRef(null);
  const [draft, setDraft] = useState(isoToDisplay(value));
  const [error, setError] = useState("");

  useEffect(() => {
    setDraft(isoToDisplay(value));
    setError("");
  }, [value]);

  const setValidity = (message = "") => {
    textRef.current?.setCustomValidity(message);
    setError(message);
  };

  const commit = (nextValue) => {
    if (!nextValue.trim()) {
      setValidity(required ? "A date is required." : "");
      onChange("");
      return;
    }
    const isoDate = displayToIso(nextValue);
    if (!isoDate) {
      setValidity("Enter a valid date as MM/DD/YYYY.");
      return;
    }
    setValidity("");
    setDraft(isoToDisplay(isoDate));
    onChange(isoDate);
  };

  const openCalendar = () => {
    try {
      if (calendarRef.current?.showPicker) {
        calendarRef.current.showPicker();
        return;
      }
    } catch (error) {
      calendarRef.current?.focus();
      return;
    }
    calendarRef.current?.focus();
  };

  return (
    <span className="typed-date-control" data-testid={`${resolvedTestId}-control`}>
      <input
        aria-label={ariaLabel}
        aria-describedby={error ? `${inputId}-error` : undefined}
        aria-invalid={Boolean(error)}
        data-testid={resolvedTestId}
        disabled={disabled}
        id={inputId}
        inputMode="numeric"
        onBlur={(event) => commit(event.target.value)}
        onChange={(event) => {
          setDraft(event.target.value);
          if (!event.target.value.trim()) {
            commit("");
          } else {
            setValidity("");
          }
        }}
        placeholder="MM/DD/YYYY"
        ref={textRef}
        required={required}
        type="text"
        value={draft}
      />
      {error && (
        <span data-testid={`${resolvedTestId}-error`} id={`${inputId}-error`} role="alert">
          {error}
        </span>
      )}
      <button
        aria-label="Open calendar"
        data-testid={`${resolvedTestId}-calendar-button`}
        disabled={disabled}
        onClick={openCalendar}
        type="button"
      >
        <CalendarDays size={16} />
      </button>
      <input
        aria-label="Choose date from calendar"
        className="typed-date-calendar-input"
        data-testid={`${resolvedTestId}-calendar-input`}
        disabled={disabled}
        onChange={(event) => {
          const isoDate = event.target.value;
          setValidity("");
          setDraft(isoToDisplay(isoDate));
          onChange(isoDate);
        }}
        ref={calendarRef}
        type="date"
        value={value || ""}
      />
    </span>
  );
};